import { isoZ, z } from 'my-zod'
import { orderPaymentSchema } from './order-payment'
import { CustomerOrderBookingMethodOptions } from './customer-order-model'
import { GeneralAddressFragment } from '../../utilities/address/general-address-fragment'
import { RideInsuranceConfigSchemas } from '../../finance/ride-insurance-config'
import { additionalQuestionResponse } from '../reservation/additional-question-response'
import { addGiftCardToCustomerOrderRequest } from './add-gift-card-to-customer-order-request'
import { customerOrderPaymentMethodSystemView } from '../customer-order-payment-method'

const customErrorMap: z.ZodErrorMap = (error, ctx) => {
  if (error.code === z.ZodIssueCode.invalid_type) {
    return {
      message: 'Incorrect date format.'
    }
  }
  return { message: ctx.defaultError }
}

export const reservationCreate = z.object({
  id: z.string(),
  productId: z.string(),
  productVersionId: z.number(),
  referringDealerId: z.number().optional(),
  beginDate: isoZ.date({ errorMap: customErrorMap }),
  endDate: isoZ.date({ errorMap: customErrorMap }),
  ridingStartTime: isoZ.time(),
  ridingEndTime: isoZ.time(),
  vehicles: z
    .object({
      /** this is the id of the reservation vehicle to be created */
      id: z.string().uuid(),
      assetFamilyId: z.string(),
      pricingOverride: z.number().int().nonnegative().nullable(),
      signature: z.string().min(1),
      /**
       * not technically the time the row was inserted into the database, rather the time
       * the customer declared their intent to book (as represented by reservation vehicle create context)
       */
      quotedDate: isoZ.instant(),
      quotedDateLocal: isoZ.date(),
      quotedTimeLocal: isoZ.time(),
      numberOfUnitsAvailable: z.number().int().nonnegative()
    })
    .array()
    .min(1, 'Required'),
  deliveryAddress: GeneralAddressFragment.nullable().optional(),
  rideInsurance: z
    .object({
      /** this is the id of the addon to be created */
      id: z.string().uuid(),
      planType: z.enum(RideInsuranceConfigSchemas.rideInsurancePlanTypeOptions)
    })
    .nullable(),
  additionalQuestionResponses: additionalQuestionResponse.array(),
  insuranceCarrier: z.string().nullish(),
  insurancePolicyNumber: z.string().nullish(),
  towingVehicleLicensePlateNumber: z.string().nullish(),
  rideInsuranceVersionId: z.number().nullable()
})
export type reservationCreate = z.infer<typeof reservationCreate>

export const createCustomerOrder = z.object({
  id: z.string(),
  shortId: z.string(),
  outfitterId: z.number(),
  cartId: z.string().nullable(),
  customer: z
    .object({
      type: z.literal('new'),
      hypotheticalCustomerId:
        z.string() /** NOTE: This may not be the customer id associated with the order. Depends on if email matches customer in db */,
      firstName: z.string().min(1, 'Required'),
      lastName: z.string().min(1, 'Required'),
      primaryPhone: z.string().min(1, 'Required'),
      transactionalSMSOptIn: z.boolean(),
      email: z.string().email(),
      addressLine1: z.string().nullish(),
      addressLine2: z.string().nullish(),
      city: z.string().nullish(),
      state: z.string().nullish(),
      postalCode: z.string().nullish(),
      country: z.string().nullish()
    })
    .or(
      z.object({
        type: z.literal('existing'),
        customerId: z.string(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        primaryPhone: z.string().optional(),
        email: z.string().email().optional(),
        addressLine1: z.string().nullish().optional(),
        addressLine2: z.string().nullish().optional(),
        city: z.string().nullish().optional(),
        state: z.string().nullish().optional(),
        postalCode: z.string().nullish().optional(),
        country: z.string().nullish().optional()
      })
    ),
  reservation: reservationCreate.nullable(),
  taxConfigurationVersionId: z.number(),
  reservationDepositAmount: z.number(),
  reservationDepositUnit: z.enum(['percentage', 'fixed', 'full']),

  utm_medium: z.string().nullable(),
  utm_source: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  bookingMethod: z.enum(CustomerOrderBookingMethodOptions),
  ignoreExistingReservations: z.boolean().optional(),
  addOns: z
    .object({
      id: z.string().uuid(),
      addOnProductId: z.string().uuid(),
      addOnProductVersionId: z.number()
    })
    .array(),
  giftCardPurchases: addGiftCardToCustomerOrderRequest.array(),
  weatherGuarantee: z
    .object({
      quoteId: z.string(),
      status: z.enum(['accept', 'decline']),
      addonId: z.string().uuid()
    })
    .nullable(),
  promotionDiscounts: z
    .object({
      promotion: z.object({
        id: z.string(),
        versionId: z.number(),
        code: z.string().nullable()
      }),
      discountId: z.string()
    })
    .array(),
  paymentEstimate: z.number(),
  orderTotalEstimate: z.number(),
  payment: orderPaymentSchema,
  additionalPaymentMethodsToAssociate: customerOrderPaymentMethodSystemView
    .omit({ customerOrderShortId: true })
    .array()
    .optional(),
  /**
   * not technically the time the row was inserted into the database, rather the time
   * the customer declared their intent to book (as represented by reservation create context)
   */
  quotedDate: isoZ.instant(),
  quotedDateLocal: isoZ.date(),
  signature: z.string().min(1)
})

export type createCustomerOrder = z.infer<typeof createCustomerOrder>
